import { getImageDimensions } from '@/utils/editor/getImageDimensions'

export type ImageQualityCalculationArgs = {
  imageUrl: string
  displayedBoxWidthPixels: number
  displayedBoxHeightPixels: number
  cropLeftPixels?: number
  cropRightPixels?: number
  cropTopPixels?: number
  cropBottomPixels?: number
  productPixelsPerInch: number
  minimumPixelsPerInch: number
  cachedOriginalImageDimensions?: { width: number; height: number } | null
}

export type ImageQualityCalculationResult = {
  effectivePixelsPerInch: number | null
  originalImagePixelSize: { width: number | null; height: number | null }
  printedSizeInches: { width: number | null; height: number | null }
  isBelowPrintQualityThreshold: boolean
}

/**
 * Pure function to calculate image quality metrics.
 *
 * Extracted from useCheckImageQuality hook to enable background processing
 * of all pages without requiring React component lifecycle.
 *
 * ## IMAGE QUALITY CALCULATION EXPLAINED
 *
 * **The Goal:** Determine if an image will look good when printed by calculating "effective PPI"
 * (how many original image pixels will be used per inch of printed output).
 *
 * **Why We Need All These Components:**
 *
 * ### 1. PRODUCT PPI - Converts canvas pixels to printed inches
 * - Product canvas: 840×696 pixels for 8.75×7.25 inches = 96 PPI
 * - This tells us: 96 canvas pixels = 1 printed inch
 *
 * ### 2. CONTAINER SIZE - How big the image appears on canvas
 * - Image container: 400×300 pixels (on the 840×696 canvas)
 * - Printed size: 400÷96 = 4.17" wide, 300÷96 = 3.125" tall
 *
 * ### 3. ORIGINAL IMAGE - How much detail we have to work with
 * - Original photo: 1200×900 pixels
 *
 * ### 4. CSS COVER SCALING - How the browser fits the image in the container
 * - Horizontal scale: 400÷1200 = 0.33 (shrink to 33%)
 * - Vertical scale: 300÷900 = 0.33 (shrink to 33%)
 * - Cover scale: max(0.33, 0.33) = 0.33 (use the larger to fill container)
 *
 * ### 5. FINAL CALCULATION - Effective pixels per printed inch
 * - Original pixels used width: 400÷0.33 = 1212 pixels of original image
 * - Original pixels used height: 300÷0.33 = 909 pixels of original image
 * - Effective PPI X: 1212÷4.17" = 290 PPI ✅ Good quality!
 * - Effective PPI Y: 909÷3.125" = 291 PPI ✅ Good quality!
 * - Final: min(290, 291) = 290 PPI
 *
 * ### Example of BAD quality:
 * - Small 300×200 pixel photo in 600×400 pixel container
 * - Container printed: 6.25"×4.17", Image scale: 2x (ENLARGED!)
 * - Effective PPI: 300÷6.25" = 48 PPI 💥 Pixelated!
 *
 * @param args - Image and container dimensions, crop data, and thresholds
 * @returns Quality metrics including effective PPI and threshold comparison
 */
export const calculateImageQuality = async ({
  imageUrl,
  displayedBoxWidthPixels,
  displayedBoxHeightPixels,
  cropLeftPixels = 0,
  cropRightPixels = 0,
  cropTopPixels = 0,
  cropBottomPixels = 0,
  productPixelsPerInch,
  minimumPixelsPerInch,
  cachedOriginalImageDimensions,
}: ImageQualityCalculationArgs): Promise<ImageQualityCalculationResult> => {
  try {
    const displayedCroppedWidthPixels = Math.max(
      0,
      displayedBoxWidthPixels - cropLeftPixels - cropRightPixels,
    )
    const displayedCroppedHeightPixels = Math.max(
      0,
      displayedBoxHeightPixels - cropTopPixels - cropBottomPixels,
    )
    const originalImageDimensions =
      cachedOriginalImageDimensions ?? (await getImageDimensions(imageUrl))
    const isValidOriginalImageDimensions =
      originalImageDimensions.width && originalImageDimensions.height
    if (!isValidOriginalImageDimensions) {
      return {
        effectivePixelsPerInch: null,
        originalImagePixelSize: { width: null, height: null },
        printedSizeInches: { width: null, height: null },
        isBelowPrintQualityThreshold: false,
      }
    }

    // Step 1: Validate productPixelsPerInch before any division operations
    const isValidProductPixelsPerInch =
      Number.isFinite(productPixelsPerInch) && productPixelsPerInch > 0
    if (!isValidProductPixelsPerInch) {
      return {
        effectivePixelsPerInch: null,
        originalImagePixelSize: {
          width: originalImageDimensions.width,
          height: originalImageDimensions.height,
        },
        printedSizeInches: { width: null, height: null },
        isBelowPrintQualityThreshold: false,
      }
    }

    // Step 2: Convert container size from canvas pixels to printed inches
    const printedWidthInches =
      displayedCroppedWidthPixels / productPixelsPerInch
    const printedHeightInches =
      displayedCroppedHeightPixels / productPixelsPerInch
    const isValidPrintedDimensions =
      printedWidthInches > 0 && printedHeightInches > 0
    if (!isValidPrintedDimensions) {
      return {
        effectivePixelsPerInch: null,
        originalImagePixelSize: {
          width: originalImageDimensions.width,
          height: originalImageDimensions.height,
        },
        printedSizeInches: { width: null, height: null },
        isBelowPrintQualityThreshold: false,
      }
    }

    // Step 3: Calculate CSS background-size cover scaling
    const horizontalCoverScale =
      displayedBoxWidthPixels / originalImageDimensions.width
    const verticalCoverScale =
      displayedBoxHeightPixels / originalImageDimensions.height
    // CSS cover uses the larger scale to ensure container is filled
    const coverScaleFactor = Math.max(horizontalCoverScale, verticalCoverScale)

    // Step 4: Calculate how many original image pixels are actually used
    const usedOriginalImageWidthPixels =
      coverScaleFactor > 0 ? displayedCroppedWidthPixels / coverScaleFactor : 0
    const usedOriginalImageHeightPixels =
      coverScaleFactor > 0 ? displayedCroppedHeightPixels / coverScaleFactor : 0

    // Step 5: Calculate effective PPI (original pixels per printed inch)
    const effectivePixelsPerInchHorizontal =
      usedOriginalImageWidthPixels / printedWidthInches
    const effectivePixelsPerInchVertical =
      usedOriginalImageHeightPixels / printedHeightInches
    // Use the limiting (worse) direction to be conservative
    const effectivePixelsPerInch = Math.min(
      effectivePixelsPerInchHorizontal,
      effectivePixelsPerInchVertical,
    )

    const isBelowPrintQualityThreshold =
      effectivePixelsPerInch < minimumPixelsPerInch

    return {
      effectivePixelsPerInch,
      originalImagePixelSize: {
        width: originalImageDimensions.width,
        height: originalImageDimensions.height,
      },
      printedSizeInches: {
        width: printedWidthInches,
        height: printedHeightInches,
      },
      isBelowPrintQualityThreshold,
    }
  } catch {
    return {
      effectivePixelsPerInch: null,
      originalImagePixelSize: { width: null, height: null },
      printedSizeInches: { width: null, height: null },
      isBelowPrintQualityThreshold: false,
    }
  }
}

/**
 * The pixel size an image needs for its placement to reach
 * `targetPixelsPerInch`, read off a result of `calculateImageQuality`, so
 * the Enhance a low-resolution warning starts asks for exactly what that
 * warning measures.
 *
 * At a fixed placement the effective PPI grows in step with the image's
 * pixel size: an image twice as large is shrunk by twice as much to cover
 * the same box, so it puts twice as many pixels into every printed inch.
 * The size needed is therefore the measured size grown by
 * target ÷ measured PPI, rounded up to whole pixels. It is never smaller
 * than the measured size. Null when the result has no measurement to grow
 * from.
 */
export const requiredImagePixelSizeForPpi = (
  result: Pick<
    ImageQualityCalculationResult,
    'effectivePixelsPerInch' | 'originalImagePixelSize'
  >,
  targetPixelsPerInch: number,
): { width: number; height: number } | null => {
  const { effectivePixelsPerInch, originalImagePixelSize } = result
  const { width, height } = originalImagePixelSize
  const canGrow =
    effectivePixelsPerInch !== null &&
    effectivePixelsPerInch > 0 &&
    Number.isFinite(effectivePixelsPerInch) &&
    width !== null &&
    height !== null &&
    width > 0 &&
    height > 0 &&
    Number.isFinite(targetPixelsPerInch) &&
    targetPixelsPerInch > 0
  if (!canGrow) return null

  const scale = Math.max(1, targetPixelsPerInch / effectivePixelsPerInch)
  return {
    width: Math.ceil(width * scale),
    height: Math.ceil(height * scale),
  }
}
